import React, { useState } from "react";
import { Box, Paper, IconButton, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import { TextGradient, SmallTextGradient, IconGradient } from "../Gradient";
import { Button } from "../Buttons";

const STORAGE_KEY = "whats-new-banner-dismissed";

const WhatsNewBanner: React.FC = () => {
  // Check localStorage synchronously to prevent layout shift
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window !== "undefined") {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      return !dismissed;
    }
    return false;
  });
  const navigate = useNavigate();
  const theme = useTheme();

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, "true");
  };

  const handleClick = () => {
    navigate("/whats-new");
  };

  if (!isVisible) return null;

  return (
    <Paper
      elevation={0}
      component={Box}
      onClick={handleClick}
      sx={{
        mb: 4,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        border: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.05)",
        cursor: "pointer",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 16px rgba(0,0,0,0.2)"
              : "0 4px 16px rgba(0,0,0,0.1)",
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(165, 180, 252, 0.2)"
                : "rgba(59, 130, 246, 0.2)"
            }`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "100%",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #a5b4fc 0%, #8b5cf6 100%)"
              : "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(165, 180, 252, 0.1)"
                  : "rgba(59, 130, 246, 0.1)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(165, 180, 252, 0.2)"
                    : "rgba(59, 130, 246, 0.2)"
                }`,
              flexShrink: 0,
            }}
          >
            <IconGradient gradientVariant="default">
              <AnnouncementIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconGradient>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SmallTextGradient
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                fontWeight: 700,
                mb: 0.5,
                display: "block",
              }}
            >
              What's New: Multi-Chapter Stories!
            </SmallTextGradient>
            <TextGradient
              sx={{
                fontSize: { xs: "0.8125rem", sm: "0.9375rem" },
                color: "text.secondary",
                display: { xs: "none", sm: "block" },
              }}
            >
              We've added multi-chapter support, redesigned story pages, and much more. Click to
              learn about all the updates!
            </TextGradient>
            <TextGradient
              sx={{
                fontSize: "0.8125rem",
                color: "text.secondary",
                display: { xs: "block", sm: "none" },
              }}
            >
              New features: Multi-chapter stories, improved UI, and more!
            </TextGradient>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            sx={{
              display: { xs: "none", sm: "flex" },
              minWidth: 0,
              px: 2,
            }}
          >
            Learn More
          </Button>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
            }}
            aria-label="Dismiss banner"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default WhatsNewBanner;

